/**
 * Centralized cache version management.
 * Bump this version to clear all IndexedDB caches on next load.
 */
export const CACHE_VERSION = 11;

const CACHE_VERSION_KEY = 'nivaas-cache-version';

/**
 * Legacy database patterns to clean up.
 * These are old IndexedDB stores from before the unified versioning system.
 */
const LEGACY_DB_PATTERNS = [
  /^nivaas-preferences-v[0-9]+$/, // Old workspace preferences (e.g. nivaas-preferences-v1)
  /^nivaas-cache-.*-v[0-9]+$/, // Old URQL caches (e.g. nivaas-cache-abc123-v9)
];

/**
 * Check if a database name matches current versioned naming.
 */
function isCurrentVersionDB(name: string): boolean {
  const versionSuffix = `-v${CACHE_VERSION}`;
  return name.endsWith(versionSuffix);
}

/**
 * Delete a single IndexedDB database by name.
 */
async function deleteDatabase(name: string): Promise<void> {
  return new Promise((resolve) => {
    const request = indexedDB.deleteDatabase(name);
    request.onsuccess = () => {
      console.log(`[Cache] Deleted database: ${name}`);
      resolve();
    };
    request.onerror = () => {
      console.warn(`[Cache] Failed to delete: ${name}`);
      resolve();
    };
    request.onblocked = () => {
      console.warn(`[Cache] Delete blocked for: ${name}`);
      resolve();
    };
  });
}

/**
 * Clean up legacy IndexedDB databases from before unified versioning.
 */
async function clearLegacyDatabases(): Promise<void> {
  if (typeof indexedDB === 'undefined') return;

  try {
    const databases = await indexedDB.databases();

    for (const db of databases) {
      if (!db.name) continue;

      // Skip current version databases
      if (isCurrentVersionDB(db.name)) continue;

      // Delete if it matches any legacy pattern
      const isLegacy = LEGACY_DB_PATTERNS.some((pattern) => pattern.test(db.name!));
      if (isLegacy) {
        await deleteDatabase(db.name);
      }
    }
  } catch (e) {
    console.warn('[Cache] Failed to clear legacy databases:', e);
  }
}

/**
 * Clear all IndexedDB databases for this app.
 */
async function clearAllIndexedDB(): Promise<void> {
  if (typeof indexedDB === 'undefined') return;

  try {
    const databases = await indexedDB.databases();
    const nivaasDBs = databases.filter((db) => db.name?.startsWith('nivaas'));

    for (const db of nivaasDBs) {
      if (db.name) {
        await deleteDatabase(db.name);
      }
    }
  } catch (e) {
    console.warn('[Cache] Failed to clear IndexedDB:', e);
  }
}

/**
 * Clear IndexedDB caches for a specific workspace.
 */
export async function clearWorkspaceCache(workspaceId: string): Promise<void> {
  if (typeof indexedDB === 'undefined' || !workspaceId) return;

  try {
    const databases = await indexedDB.databases();
    const workspaceDBs = databases.filter((db) => db.name?.includes(`-${workspaceId}-`));

    for (const db of workspaceDBs) {
      if (db.name) await deleteDatabase(db.name);
    }
  } catch (e) {
    console.warn('[Cache] Failed to clear workspace cache:', e);
  }
}

/**
 * Initialize cache version check. Call once on app load.
 * Clears all caches if version has changed, and always cleans up legacy databases.
 */
export async function initializeCacheVersion(): Promise<void> {
  if (typeof localStorage === 'undefined') return;

  // Always clean up old legacy databases (from before unified versioning)
  await clearLegacyDatabases();

  const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);
  const currentVersion = String(CACHE_VERSION);

  if (storedVersion !== currentVersion) {
    console.log(
      `[Cache] Version changed from ${storedVersion} to ${currentVersion}, clearing all caches...`,
    );
    await clearAllIndexedDB();
    localStorage.setItem(CACHE_VERSION_KEY, currentVersion);
    console.log('[Cache] All caches cleared');
  }
}
